import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  FormControlLabel,
  Switch,
  CircularProgress,
  MenuItem,
  TextField,
  Checkbox,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import { toast } from 'react-toastify';
import {
  Announcement,
  Person4,
  SettingsRounded,
  TrackChanges,
  Business,
  ContactMail,
  RateReview,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdminTabs } from '../AdminTabs';
import {
  getWorkflowSettings,
  getTimezoneOptions,
  getDateFormatOptions,
  saveWorkflowSettings,
  getOrganizationById,
  updateOrganization,
} from '../../apis/Admin';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';

// ---------- StyledTextField ----------
const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    height: 45,
    alignItems: 'center',
    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#5965A6',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#5965A6',
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: '#d32f2f',
  },
}));

// Adapter: API -> UI
const adaptFromApi = (api) => ({
  timezone: api?.default_timezone ?? 'UTC',
  dateFormat: api?.date_format ?? 'YYYY-MM-DD',
  showUtc: !!api?.also_show_times_in_utc,
  secondaryTimezone: api?.secondary_timezone ?? 'UTC',
});

// Adapter: UI -> API payload
const adaptToApi = (st) => ({
  default_timezone: st.timezone,
  also_show_times_in_utc: !!st.showUtc,
  secondary_timezone: st.showUtc ? null : st.secondaryTimezone || null,
  date_format: st.dateFormat,
});

const LoadingMenuItem = ({ text = 'Loading...' }) => (
  <MenuItem value="__loading" disabled>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={18} />
      <Typography variant="body2">{text}</Typography>
    </Box>
  </MenuItem>
);

const EmptyMenuItem = ({ text = 'No options available' }) => (
  <MenuItem value="__empty" disabled>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </MenuItem>
);

const AdminSetting = () => {
  // Super admin check
  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const defaultSettings = {
    timezone: 'UTC',
    dateFormat: 'YYYY-MM-DD',
    showUtc: false,
    secondaryTimezone: 'UTC',

    concurrency: 1,
    notifyOnSuccess: false,
    notifyOnFailure: true,
    webhookUrl: '',
    logRetentionDays: 14,
    retainArtifacts: true,
    autoMapFields: true,
    fuzzyThreshold: 0.7,
    batchSize: 500,
    paginationSize: 1000,
    errorPolicy: 'continue',
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [tzOptions, setTzOptions] = useState([]);
  const [fmtOptions, setFmtOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [ipWhitelistEnabled, setIpWhitelistEnabled] = useState(false);
  const [allowedIpRanges, setAllowedIpRanges] = useState([]);
  const [newIpRange, setNewIpRange] = useState('');
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [ipError, setIpError] = useState('');

  const autoFormatIpAddress = (input) => {
    if (!input || !input.trim()) {
      return input;
    }

    const trimmed = input.trim();
    
    if (trimmed.includes('.')) {
      return trimmed;
    }

    const cidrMatch = trimmed.match(/^(\d+)(\/\d+)?$/);
    if (!cidrMatch) {
      return trimmed;
    }

    const digitsOnly = cidrMatch[1];
    const cidrPart = cidrMatch[2] || '';

    if (digitsOnly.length < 4) {
      return trimmed; 
    }

    let remaining = digitsOnly;
    const octets = [];
    
    if (digitsOnly.length === 12) {
      for (let i = 0; i < 4; i++) {
        octets.push(digitsOnly.substring(i * 3, (i + 1) * 3));
      }
    } else {
      for (let i = 0; i < 4; i++) {
        if (remaining.length === 0) {
          octets.push('0');
        } else if (i === 3) {
          const lastOctet = remaining.length > 3 ? remaining.substring(0, 3) : remaining;
          octets.push(lastOctet);
          remaining = '';
        } else {
          const digitsNeeded = 4 - i - 1;
          const available = remaining.length - digitsNeeded;
          
          if (available <= 0) {
            octets.push('0');
          } else {
            let take = Math.min(3, available);
            
            if (take === 3) {
              const threeDigits = parseInt(remaining.substring(0, 3), 10);
              if (threeDigits > 255) {
                take = 2;
                const twoDigits = parseInt(remaining.substring(0, 2), 10);
                if (twoDigits > 255) {
                  take = 1;
                }
              }
            }
            
            octets.push(remaining.substring(0, take));
            remaining = remaining.substring(take);
          }
        }
      }
    }

    const formatted = octets.join('.');
    return formatted + cidrPart;
  };

  const validateIpRange = (ipRange) => {
    if (!ipRange || !ipRange.trim()) {
      return { valid: false, error: 'IP range cannot be empty', formatted: '' };
    }

    const formatted = autoFormatIpAddress(ipRange.trim());
    const trimmed = formatted.trim();
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    
    if (!ipv4Regex.test(trimmed)) {
      return { valid: false, error: 'Invalid IP format. Use format: 192.168.1.1 or 192.168.1.0/24', formatted };
    }

    if (trimmed.includes('/')) {
      const [, cidr] = trimmed.split('/');
      const cidrNum = parseInt(cidr, 10);
      if (isNaN(cidrNum) || cidrNum < 0 || cidrNum > 32) {
        return { valid: false, error: 'CIDR must be between 0 and 32', formatted };
      }
    }

    const parts = trimmed.split('/')[0].split('.');
    for (const part of parts) {
      const num = parseInt(part, 10);
      if (isNaN(num) || num < 0 || num > 255) {
        return { valid: false, error: 'Each IP octet must be between 0 and 255', formatted };
      }
    }

    return { valid: true, error: '', formatted };
  };

  const [orgId] = useState(() => {
    const v = Cookies.get('Org_id');
    return (typeof v === 'string' && v.trim()) || 'default_org';
  });

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setLoadingOrg(true);
      try {
        const [tzRes, fmtRes, wsRes, orgRes] = await Promise.all([
          getTimezoneOptions(orgId),
          getDateFormatOptions(orgId),
          getWorkflowSettings(orgId),
          !isSuperAdmin ? getOrganizationById(orgId).catch(() => null) : Promise.resolve(null),
        ]);

        if (ignore) return;

        setTzOptions(Array.isArray(tzRes) ? tzRes : []);
        setFmtOptions(Array.isArray(fmtRes) ? fmtRes : []);

        const apiSettings = wsRes?.workflow_settings || {};
        const uiState = adaptFromApi(apiSettings);
        setSettings((prev) => ({ ...prev, ...uiState }));

        if (!isSuperAdmin && orgRes) {
          setIpWhitelistEnabled(orgRes.ip_whitelist_enabled || false);
          setAllowedIpRanges(orgRes.allowed_ip_ranges || []);
        }

        localStorage.setItem(
          'etlSettings',
          JSON.stringify({ ...settings, ...uiState }),
        );
      } catch (e) {
        console.warn('Settings load failed, falling back to localStorage', e);
        toast.error(
          (typeof e?.response?.data === 'string' && e.response.data) ||
            e?.response?.data?.detail ||
            e?.response?.data?.message ||
            e?.message ||
            'Request failed',
        );
        try {
          const saved = localStorage.getItem('etlSettings');
          if (saved) {
            const parsed = JSON.parse(saved);
            setSettings({ ...defaultSettings, ...parsed });
          }
        } catch (err) {
          console.warn('Failed to parse saved etlSettings', err);
        }
      } finally {
        setLoading(false);
        setLoadingOrg(false);
      }
    };

    load();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line
  }, [orgId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { workflow_settings: adaptToApi(settings) };
      await saveWorkflowSettings(orgId, payload);
      
      if (!isSuperAdmin) {
        await updateOrganization(orgId, {
          ip_whitelist_enabled: ipWhitelistEnabled,
          allowed_ip_ranges: ipWhitelistEnabled && allowedIpRanges.length > 0 
            ? allowedIpRanges 
            : null,
        });
      }
      
      toast.success('Settings saved');
    } catch (e) {
      console.error(e);
      toast.error(
        (typeof e?.response?.data === 'string' && e.response.data) ||
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          'Request failed',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(defaultSettings);
    try {
      localStorage.setItem('etlSettings', JSON.stringify(defaultSettings));
    } catch (e) {
      console.warn('Failed to persist default settings to localStorage', e);
    }
    toast.info('Settings reset to defaults');
  };

  const updateField = (key) => (event) => {
    const value =
      event?.target?.type === 'checkbox'
        ? event.target.checked
        : event?.target?.value;
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const safeTzOptions = Array.isArray(tzOptions)
    ? tzOptions.filter(Boolean)
    : [];
  const safeFmtOptions = Array.isArray(fmtOptions)
    ? fmtOptions.filter(Boolean)
    : [];

  const selectedPrimaryTz = safeTzOptions.some(
    (o) => o?.timezone_name === settings.timezone,
  )
    ? settings.timezone
    : '';

  const selectedSecondaryTz = safeTzOptions.some(
    (o) => o?.timezone_name === settings.secondaryTimezone,
  )
    ? settings.secondaryTimezone
    : '';

  const selectedDateFmt = safeFmtOptions.some(
    (o) => o?.format_code === settings.dateFormat,
  )
    ? settings.dateFormat
    : '';

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Sidebar />

      <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, bgcolor: '#F7F9FF' }}>
        <AdminTabs
          items={[
            ...(isSuperAdmin
              ? [
                  {
                    to: '/admin/organizations',
                    label: 'Organizations',
                    icon: Business,
                  },
                ]
              : []),
            { to: '/admin/user', label: 'User Management', icon: Person4 },
            {
              to: '/admin/annoucement',
              label: 'Annoucement',
              icon: Announcement,
            },
            {
              to: '/admin/setting',
              label: 'Application Settings',
              icon: SettingsRounded,
            },
            { to: '/admin/audit-log', label: 'Audit Logs', icon: TrackChanges },
            ...(isSuperAdmin
              ? [
                  {
                    to: '/admin/contact-submissions',
                    label: 'Contact Submissions',
                    icon: ContactMail,
                  },
                  {
                    to: '/admin/public-reviews',
                    label: 'Public Reviews',
                    icon: RateReview,
                  },
                ]
              : []),
          ]}
        />
        <Box
          sx={{
            minHeight: 'calc(100vh - 5rem)',
            overflow: 'auto',
            p: { xs: 2, sm: 3 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: '#111827',
                letterSpacing: '0.03em',
                lineHeight: 1.2,
              }}
            >
              Application Settings
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure your connection settings
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 26,
                      bgcolor: '#5965A6',
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 600,
                      color: '#111827',
                      letterSpacing: '0.03em',
                      lineHeight: 1.2,
                    }}
                  >
                    Workflow
                  </Typography>
                </Box>

                <Grid container spacing={4}>
                  {/* Default Timezone */}
                  <Grid item xs={12} sm={12}>
                    <StyledTextField
                      select
                      fullWidth
                      size="small"
                      label="Default Timezone"
                      value={selectedPrimaryTz}
                      onChange={updateField('timezone')}
                    >
                      {loading && (
                        <LoadingMenuItem text="Loading timezones..." />
                      )}
                      {!loading && safeTzOptions.length === 0 && (
                        <EmptyMenuItem text="No timezones found" />
                      )}
                      {!loading &&
                        safeTzOptions.length > 0 &&
                        safeTzOptions.map((tz) => (
                          <MenuItem
                            key={tz.timezone_id}
                            value={tz.timezone_name}
                          >
                            {tz.display_name || tz.timezone_name}
                          </MenuItem>
                        ))}
                    </StyledTextField>
                  </Grid>

                  {/* Skip secondary timezone switch */}
                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!settings.showUtc}
                          onChange={(e) =>
                            updateField('showUtc')({
                              target: { value: e.target.checked },
                            })
                          }
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#5965A6',
                              '&:hover': {
                                backgroundColor: 'rgba(89,101,166,0.08)',
                              },
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#aeb9fcff',
                                opacity: 1,
                              },
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: '#868687ff',
                              opacity: 1,
                            },
                          }}
                        />
                      }
                      label="Skip secondary timezone"
                      sx={{ ml: 0 }}
                    />
                  </Grid>

                  {/* Secondary Timezone (hidden when showUtc is true) */}
                  {!settings.showUtc && (
                    <Grid item xs={12} sm={12}>
                      <StyledTextField
                        select
                        fullWidth
                        size="small"
                        label="Secondary Timezone"
                        value={selectedSecondaryTz}
                        onChange={updateField('secondaryTimezone')}
                      >
                        {loading && (
                          <LoadingMenuItem text="Loading timezones..." />
                        )}
                        {!loading && safeTzOptions.length === 0 && (
                          <EmptyMenuItem text="No timezones found" />
                        )}
                        {!loading &&
                          safeTzOptions.length > 0 &&
                          safeTzOptions.map((tz) => (
                            <MenuItem
                              key={tz.timezone_id}
                              value={tz.timezone_name}
                            >
                              {tz.display_name || tz.timezone_name}
                            </MenuItem>
                          ))}
                      </StyledTextField>
                    </Grid>
                  )}

                  {/* Date Format */}
                  <Grid item xs={12} sm={12}>
                    <StyledTextField
                      select
                      fullWidth
                      size="small"
                      label="Date Format"
                      value={selectedDateFmt}
                      onChange={updateField('dateFormat')}
                    >
                      {loading && (
                        <LoadingMenuItem text="Loading date formats..." />
                      )}
                      {!loading && safeFmtOptions.length === 0 && (
                        <EmptyMenuItem text="No date formats found" />
                      )}
                      {!loading &&
                        safeFmtOptions.length > 0 &&
                        safeFmtOptions.map((fmt) => (
                          <MenuItem
                            key={fmt.format_id}
                            value={fmt.format_code}
                          >
                            {fmt.format_code}
                          </MenuItem>
                        ))}
                    </StyledTextField>
                  </Grid>
                </Grid>
              </Paper>

              {!isSuperAdmin && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 26,
                      bgcolor: '#5965A6',
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 600,
                      color: '#111827',
                      letterSpacing: '0.03em',
                      lineHeight: 1.2,
                    }}
                  >
                    IP Whitelist (Optional)
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ipWhitelistEnabled}
                      onChange={(e) => setIpWhitelistEnabled(e.target.checked)}
                      disabled={loadingOrg || saving}
                    />
                  }
                  label="Enable IP Whitelist"
                  sx={{ mb: 1.5 }}
                />

                {ipWhitelistEnabled && (
                  <>
                    <Typography variant="caption" sx={{ mb: 2, color: '#6b7280', display: 'block' }}>
                      Restrict access to specific IP addresses or ranges. Only requests from these IPs will be allowed.
                      <br />
                      Format: Single IP (e.g., 192.168.1.1) or CIDR range (e.g., 192.168.1.0/24)
                    </Typography>
                  </>
                )}

                {!ipWhitelistEnabled && (
                  <Typography variant="caption" sx={{ mb: 2, color: '#9ca3af', fontStyle: 'italic', display: 'block' }}>
                    IP whitelist is disabled. All IP addresses are allowed.
                  </Typography>
                )}

                {ipWhitelistEnabled && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g., 192.168.1.0/24 or 10.0.0.1"
                        value={newIpRange}
                        onChange={(e) => {
                          setNewIpRange(e.target.value);
                          if (ipError) setIpError('');
                        }}
                        onBlur={() => {
                          if (newIpRange.trim()) {
                            const formatted = autoFormatIpAddress(newIpRange.trim());
                            setNewIpRange(formatted);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const trimmed = newIpRange.trim();
                            if (trimmed) {
                              const validation = validateIpRange(trimmed);
                              if (!validation.valid) {
                                setIpError(validation.error);
                                return;
                              }
                              setIpError('');
                              if (!allowedIpRanges.includes(validation.formatted)) {
                                setAllowedIpRanges([...allowedIpRanges, validation.formatted]);
                                setNewIpRange('');
                              } else {
                                toast.warning('IP range already added');
                              }
                            }
                          }
                        }}
                        error={!!ipError}
                        helperText={ipError}
                        disabled={saving}
                        sx={{ flexGrow: 1 }}
                        fullWidth
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const trimmed = newIpRange.trim();
                          if (trimmed) {
                            const validation = validateIpRange(trimmed);
                            if (!validation.valid) {
                              setIpError(validation.error);
                              toast.error(validation.error);
                              return;
                            }
                            setIpError('');
                            if (!allowedIpRanges.includes(validation.formatted)) {
                              setAllowedIpRanges([...allowedIpRanges, validation.formatted]);
                              setNewIpRange('');
                            } else {
                              toast.warning('IP range already added');
                            }
                          }
                        }}
                        disabled={saving || !newIpRange.trim()}
                        sx={{ borderColor: '#5965A6', color: '#5965A6' }}
                      >
                        Add
                      </Button>
                    </Box>

                    {allowedIpRanges.length > 0 && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          bgcolor: '#F8FAFF',
                          borderColor: '#E5E7EB',
                          maxHeight: 150,
                          overflow: 'auto',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {allowedIpRanges.map((ipRange, index) => (
                            <Chip
                              key={index}
                              label={ipRange}
                              onDelete={() => {
                                const updated = [...allowedIpRanges];
                                updated.splice(index, 1);
                                setAllowedIpRanges(updated);
                              }}
                              deleteIcon={<DeleteIcon />}
                              sx={{
                                bgcolor: '#EEF2FF',
                                color: '#5965A6',
                                '& .MuiChip-deleteIcon': {
                                  color: '#5965A6',
                                  '&:hover': { color: '#d32f2f' },
                                },
                              }}
                              disabled={saving}
                            />
                          ))}
                        </Box>
                      </Paper>
                    )}

                    {allowedIpRanges.length === 0 && (
                      <Typography variant="caption" sx={{ color: '#ed6c02', fontStyle: 'italic' }}>
                        ⚠️ No IP ranges added. When enabled, all IPs will be blocked unless added here.
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
              )}
            </Grid>
          </Grid>

          <Box
            sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}
          >
            <Button
              variant="outlined"
              size="medium"
              sx={{ color: '#5965A6',textTransform: 'capitalize' }}
              onClick={handleReset}
              disabled={loading || saving}
            >
              Reset
            </Button>
            <Button
              size="medium"
              variant="contained"
              onClick={handleSave}
              sx={{ bgcolor: '#5965A6',textTransform: 'capitalize' }}
              disabled={loading || saving}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

LoadingMenuItem.propTypes = {
  text: PropTypes.string,
};
EmptyMenuItem.propTypes = {
  text: PropTypes.string,
};

export default AdminSetting;
